<template>
  <div :class="classes">
    <template v-for="(option, index) in inputOptions" :key="index">
      <ak-checkbox
        ref="checkboxes"
        v-model="option.model"
        :true-value="option.value"
        :false-value="undefined"
        :label="true"
        :required="required"
        :is-group="true"
        class="ak-checkbox-group__item"
      >
        {{ option.label }}
      </ak-checkbox>
    </template>
  </div>
</template>

<script>
import {
  defineComponent,
  computed,
  ref,
  onMounted,
  onBeforeUnmount,
  watch,
} from 'vue'

import {
  checkRadioGroupProps,
  validationProps,
} from '../../mixins/formPropsMixin'

import { isObject, hasOwn } from '../../utils/helpers.js'
import Checkbox from '../checkbox/Checkbox.vue'
import validationMixin from '../../mixins/validationMixin'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-radio-group :options="options"></ak-radio-group>
   */
  name: 'AkCheckboxGroup',

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [Checkbox.name]: Checkbox,
  },

  /**
   * @return Object
   * @description Provides the returned object to the components decendants.
   */
  provide() {
    return {
      akCheckboxGroup: this,
    }
  },

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    ...checkRadioGroupProps,
    ...validationProps,
  },

  /**
   * @type {Array}
   * @description The list of events that this component can trigger
   */
  emits: ['update:modelValue'],

  /**
   * @param {Object} props - The properties of the component
   * @param {Object} options - The setup options
   * @param {Function} options.emit - The emit method for triggering events
   * @param {Function} options.attrs - The attributes for the component.
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props, { emit, attrs }) {
    const inputOptions = ref([])
    const isCheckboxGroup = ref(true)
    const checkboxes = ref(null)
    const isValid = ref(true)
    const computedValue = computed({
      get() {
        return props.modelValue
      },
      set(value) {
        emit('update:modelValue', value)
      },
    })

    const classes = computed(() => {
      return {
        'ak-checkbox-group': true,
        'ak-checkbox-group--vertical': props.vertical,
      }
    })

    const input = ref({
      type: 'checkboxGroup',
      validationMessage:
        props.validationMessage ||
        'Please choose an option if you want to proceed.',
      customRuleValidationMessage: '',
      setCustomValidity(message) {
        this.customRuleValidationMessage = message
        if (message.length > 0) isValid.value = false
        else isValid.value = true
      },
      checkValidity() {
        const hasCustomRule =
          hasOwn(props, 'customRules') && props.customRules.length > 0
        if (hasCustomRule && !isValid.value) {
          this.validationMessage =
            this.customRuleValidationMessage.length > 0
              ? this.customRuleValidationMessage
              : this.validationMessage
          return false
        }
        if (Array.isArray(checkboxes.value)) {
          return checkboxes.value
            .map((i) => i.input.checkValidity())
            .some((i) => i === true)
        } else {
          return false
        }
      },
    })

    /**
     * @type {Object}
     * @description The validation mixin return value, this is an object
     * containing all of the required functionality to handle the
     * validation of the input
     */
    const validation = validationMixin({
      type: 'checkboxGroup',
      emit,
      props,
      attrs,
      input,
      value: computedValue,
    })

    onMounted(() => {
      inputOptions.value = props.options.map((option) => {
        const value = isObject(option) ? option.value : option
        const label = isObject(option) ? option.label : option
        let model = Array.isArray(computedValue.value)
          ? computedValue.value.find((i) => i === value)
          : null
        return { value, label, model }
      })

      validation.validate()
    })

    onBeforeUnmount(() => {
      validation.beforeUnmount()
    })

    watch(computedValue, (newVal) => {
      inputOptions.value.forEach((option) => {
        const opt = newVal.find((i) => i === option.value)
        option.model = opt
      })
    })

    watch(
      inputOptions,
      () => {
        const values = inputOptions.value.map((input) => input.model)
        computedValue.value = Object.values(values).filter(
          (value) => value && value !== false
        )
      },
      {
        deep: true,
      }
    )

    return {
      computedValue,
      inputOptions,
      classes,
      attrs,
      checkboxes,
      isCheckboxGroup,
      input,
      ...validation,
    }
  },
})
</script>

<style lang="scss" rel="stylesheet/scss">
@import '../../scss/_settings.scss';
.ak-checkbox-group {
  display: flex;
  flex-wrap: wrap;
  margin-top: -$spacing-small;

  &__item,
  .ak-checkbox {
    padding-top: $spacing-small;
    margin-right: $spacing;
  }

  &--vertical {
    flex-direction: column;
  }
}
</style>
